import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, exportCsv } from "@/lib/api";
import { inr, num } from "@/lib/format";
import { PageLoader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Users, KeyRound, Trash2, Loader2, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, Search } from "lucide-react";

export default function Authors() {
  const qc = useQueryClient();
  const { data: authors, isLoading } = useQuery({
    queryKey: ["authors"],
    queryFn: async () => (await api.get("/admin/authors")).data,
  });

  const [search, setSearch] = useState("");

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", name: "", email: "", password: "" });
  const [resetFor, setResetFor] = useState(null);
  const [newPass, setNewPass] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const bulkUpload = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/admin/authors/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (data) => {
      setBulkResult(data);
      toast.success(`${data.processed} of ${data.total_rows} authors added`);
      qc.invalidateQueries({ queryKey: ["authors"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/authors/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "authors_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  };

  const create = useMutation({
    mutationFn: async () => (await api.post("/admin/authors", form)).data,
    onSuccess: () => {
      toast.success("Author account created");
      setOpen(false);
      setForm({ user_id: "", name: "", email: "", password: "" });
      qc.invalidateQueries({ queryKey: ["authors"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const reset = useMutation({
    mutationFn: async () => (await api.post(`/admin/authors/${resetFor.id}/reset-password`, { password: newPass })).data,
    onSuccess: () => {
      toast.success("Password updated");
      setResetFor(null);
      setNewPass("");
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/authors/${id}`)).data,
    onSuccess: () => {
      toast.success("Author removed");
      qc.invalidateQueries({ queryKey: ["authors"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Manage authors</p>
          <p className="text-sm text-muted-foreground mt-1">Create login accounts and manage your authors.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="export-authors" onClick={() => exportCsv("/admin/authors/export", "authors.csv").catch(() => toast.error("Export failed"))} className="gap-2">
            <Download className="h-4 w-4" /> Export
          </Button>
          <Button variant="outline" data-testid="bulk-author-button" onClick={() => { setBulkResult(null); setBulkOpen(true); }} className="gap-2">
            <Upload className="h-4 w-4" /> Bulk Import
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-author-button" className="gap-2">
                <UserPlus className="h-4 w-4" /> Add Author
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-head">New author account</DialogTitle>
                <DialogDescription>The author signs in with this User ID and password.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Full name</Label>
                  <Input data-testid="author-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ananya Rao" />
                </div>
                <div className="space-y-2">
                  <Label>User ID (login)</Label>
                  <Input data-testid="author-userid" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} placeholder="ananya" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input data-testid="author-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ananya@email.com" />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input data-testid="author-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Set a password" />
                </div>
              </div>
              <DialogFooter>
                <Button
                  data-testid="save-author-button"
                  onClick={() => create.mutate()}
                  disabled={create.isPending || !form.user_id || !form.name || !form.email || !form.password}
                >
                  {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-head">Bulk import authors</DialogTitle>
            <DialogDescription>Upload a CSV/XLSX with columns: user_id, name, email, password.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Button variant="outline" className="gap-2" data-testid="download-authors-template" onClick={downloadTemplate}>
              <Download className="h-4 w-4" /> Download template
            </Button>
            <label htmlFor="bulk-author-file" className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl py-10 cursor-pointer hover:bg-muted/40 transition-colors">
              {bulkUpload.isPending ? <Loader2 className="h-7 w-7 text-primary animate-spin" /> : <FileSpreadsheet className="h-7 w-7 text-muted-foreground" strokeWidth={1.5} />}
              <p className="text-sm font-medium">Choose a .csv or .xlsx file</p>
              <input
                id="bulk-author-file" type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="bulk-author-file-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setBulkResult(null); bulkUpload.mutate(f); }
                  e.target.value = "";
                }}
              />
            </label>
            {bulkResult && (
              <div data-testid="bulk-author-result" className="rounded-lg border border-border p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> <span className="font-medium">{bulkResult.processed}</span> of {bulkResult.total_rows} authors added.</div>
                {bulkResult.errors?.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-destructive"><AlertCircle className="h-4 w-4" /> {bulkResult.errors.length} row(s) had issues:</div>
                    <ul className="text-xs text-muted-foreground list-disc pl-6 max-h-40 overflow-y-auto">
                      {bulkResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <PageLoader />
      ) : authors.length === 0 ? (
        <EmptyState icon={Users} title="No authors yet" desc="Add your first author to start assigning books and tracking royalties." />
      ) : (
        <>
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="author-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search authors by name or ID…" className="pl-9" />
          </div>
          {(() => {
            const q = search.trim().toLowerCase();
            const filtered = q ? authors.filter((a) => a.name.toLowerCase().includes(q) || a.user_id.toLowerCase().includes(q) || (a.email || "").toLowerCase().includes(q)) : authors;
            if (filtered.length === 0) return <EmptyState icon={Search} title="No matches" desc="No authors match your search." />;
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map((a) => (
            <Card key={a.id} data-testid={`author-card-${a.user_id}`} className="p-6 hover:-translate-y-1 hover:shadow-md transition-transform duration-200">
              <div className="flex items-center gap-3">
                <Avatar className="h-11 w-11">
                  {a.avatar_url && <AvatarImage src={a.avatar_url} />}
                  <AvatarFallback className="bg-secondary text-secondary-foreground font-semibold">
                    {a.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="font-medium truncate">{a.name}</p>
                  <p className="text-xs text-muted-foreground truncate">@{a.user_id} · {a.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-5 text-center">
                <div className="bg-muted/50 rounded-lg py-2.5">
                  <p className="font-head font-bold tabular-nums">{a.book_count}</p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Books</p>
                </div>
                <div className="bg-muted/50 rounded-lg py-2.5">
                  <p className="font-head font-bold tabular-nums">{num(a.stats.total_copies)}</p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Copies</p>
                </div>
                <div className="bg-muted/50 rounded-lg py-2.5">
                  <p className="font-head font-bold tabular-nums text-primary">{inr(a.stats.total_profit)}</p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Profit</p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" className="flex-1 gap-1.5" data-testid={`reset-pass-${a.user_id}`} onClick={() => setResetFor(a)}>
                  <KeyRound className="h-3.5 w-3.5" /> Reset
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1.5 text-destructive hover:text-destructive" data-testid={`delete-author-${a.user_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove {a.name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently deletes the author along with their books, sales, redemptions and orders. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => del.mutate(a.id)}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </Card>
          ))}
        </div>
            );
          })()}
        </>
      )}

      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-head">Reset password</DialogTitle>
            <DialogDescription>Set a new password for {resetFor?.name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>New password</Label>
            <Input data-testid="new-password-input" value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="New password" />
          </div>
          <DialogFooter>
            <Button data-testid="confirm-reset-button" onClick={() => reset.mutate()} disabled={!newPass || reset.isPending}>
              {reset.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
